import { Decimal } from 'decimal.js-light';
import { fromPairs } from 'lodash';
import { IExpression } from '../IExpression';
import { isObjectsArrayField } from '../datatype/Datatype';
import { IReference, IReferenceValue } from '../references/IReference';
import { expressionLogger } from '../utils/expressionLogger';
import { IEvaluationVariables } from './variables/IEvaluationVariables';

export interface IEvaluationMathOptions {
  /**
   * Specifica se è possibile eseguire operazioni matematiche con valori nulli.
   * In quel caso devono essere gestite dall'utente eventuali coaleshing
   */
  allowsMathWithNulls?: boolean;

  /**
   * Considera i valori nulli come zero
   */
  considerNullsAsZero?: boolean;
}
export interface IEvaluationScope {
  variables: IEvaluationVariables;
  math?: IEvaluationMathOptions;
  parent?: {
    reference: IReference;
    value: IReferenceValue;
  };
}

/**
 * Contesto di valutazione delle espressioni, contiene le variabili disponibili
 * per la sostituzione dei riferimenti.
 * Permette di estendere il contesto di valutazione con nuove variabili.
 * Permette di valutare un'espressione con le variabili disponibili, in modo
 * da gestire la valutazione ricorsiva delle espressioni.
 */
export class EvaluationScope implements IEvaluationScope {
  variables: IEvaluationVariables = {};
  math: IEvaluationMathOptions = {};
  parent?: {
    reference: IReference;
    value: IReferenceValue;
  };

  /**
   * Funzione di valutazione delle espressioni, permette l'utilizzo ricorsivo
   * della valutazione delle espressioni.
   */
  private evaluateFn: (expr: IExpression, scope: EvaluationScope) => any;

  constructor(
    options: IEvaluationScope,
    evaluateFn: (expr: IExpression, scope: EvaluationScope) => any,
  ) {
    this.variables = options.variables;
    this.math = {
      considerNullsAsZero: true,
      allowsMathWithNulls: false,
      ...options.math,
    };
    this.parent = options.parent;
    this.evaluateFn = evaluateFn;
  }

  /**
   * Estende il contesto di valutazione con nuove variabili.
   * @param nestedScope Contesto di valutazione da estendere
   * @returns Il contesto di valutazione esteso
   */
  clone(nestedScope: Partial<IEvaluationScope> = {}) {
    return new EvaluationScope(
      {
        variables: { ...this.variables, ...nestedScope.variables },
        math: { ...this.math, ...nestedScope.math },
        parent: nestedScope.parent ?? this.parent,
      },
      this.evaluateFn,
    );
  }

  /**
   * Crea un nuovo contesto di valutazione figlio, in cui le variabili
   * disponibili sono quelle del contesto corrente più quelle fornite.
   *
   * Il problema è che non possiamo semplicemente aggiungere le variabili,
   * perché mentre l'array è fatto nella forma [ { nome_figlio: "..." }],
   * Le variabili vengono utilizzata come riferimento per ID, quindi ci servono
   * nella forma { "0f7bd-123": "..." }
   */
  addChildVariablesFromParent(item: Record<string, IReferenceValue>) {
    const parentField = this.parent?.reference.field;
    if (!isObjectsArrayField(parentField)) {
      expressionLogger.error(`[EvaluationScope.addChildVariablesFromParent] Parent is not an object array field`, { scope: this }); // prettier-ignore
      return this.clone();
    }

    const itemVariables = fromPairs(
      Object.keys(item ?? []).map(key => [
        parentField.structure.find(r => r.code === key)?.id,
        item[key],
      ]),
    ) as Record<string, IReferenceValue>;

    expressionLogger.debug(`[EvaluationScope.addChildVariablesFromParent] Aggiunte variabili dall'item`, { scope: this, itemVariables }); // prettier-ignore

    return this.clone({
      variables: { ...this.variables, ...itemVariables },
    });
  }

  /**
   * Crea un nuovo contesto di valutazione figlio, in cui il valore corrente
   * (es. una lista) viene salvato con un nome specifico.
   */
  createChild(parentReference: IReference) {
    const parentValue = this.getReference(parentReference);
    return this.clone({
      parent: {
        reference: parentReference,
        value: parentValue,
      },
    });
  }

  /**
   * Ottiene il valore e il riferimento corrente del padre per lo scope figlio
   */
  getParent() {
    return this.parent;
  }

  /**
   * !ATTENZIONE! Questo valore deve essere uguale a quello presente in omissisConstants.ts
   * Non possiamo importarlo perché il package engine non ha dipendenze con il package common-web.
   */
  isOmissisValue(value: any) {
    return value === '******';
  }

  /**
   * Ottiene il valore di un riferimento all'interno del contesto di
   * valutazione.
   * @param reference Riferimento da valutare
   * @param defaultValue Valore di default da restituire se il riferimento
   * non è definito
   */
  getReference(reference: IReference, defaultValue?: any) {
    const value = this.variables[reference.id];

    // Se il valore è omissis, restituisco null
    if (this.isOmissisValue(value)) {
      return null;
    }

    // Se il valore estratto è un decimal, lo riconverto in number
    // così da poterlo usare in operazioni matematiche
    if (value instanceof Decimal) {
      return value.toNumber();
    }

    return value ?? defaultValue;
  }

  /**
   * Ottiene tutti i riferimenti disponibili nel contesto di valutazione.
   */
  getAllReferences() {
    return this.variables;
  }

  /**
   * Valuta un'espressione. Se viene fornito un contesto di valutazione,
   * questo viene esteso con le variabili disponibili nel contesto corrente.
   */
  evaluate(expr: IExpression, nestedCtx?: IEvaluationScope) {
    return this.evaluateFn(expr, nestedCtx ? this.clone(nestedCtx) : this);
  }
}
